import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EventsService from "../services/EventsService";

function CreateSharedEventPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [actions, setActions] = useState([{ name: "", icon: "" }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const addAction = () => setActions([...actions, { name: "", icon: "" }]);
  const removeAction = (i) => setActions(actions.filter((_, idx) => idx !== i));
  const updateAction = (i, field, value) => {
    const u = [...actions];
    u[i] = { ...u[i], [field]: value };
    setActions(u);
  };

  const handleCreate = async () => {
    setError(null);
    if (!name.trim()) { setError(t("createSharedEvent.errEventName")); return; }
    const validActions = actions.filter((a) => a.name.trim());
    if (validActions.length === 0) { setError(t("createSharedEvent.errAtLeastOneAction")); return; }
    setSaving(true);
    try {
      const result = await EventsService.createSharedEvent({
        name: name.trim(),
        description: description.trim() || undefined,
        actions: validActions.map((a) => ({ name: a.name.trim(), icon: a.icon || undefined })),
      });
      navigate(`/shared-event/${result.id}`);
    } catch (err) {
      setError(err?.message || t("createSharedEvent.errFailed"));
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-header">
        <nav className="navbar navbar-expand-lg d-flex justify-content-between">
          <div className="header-title flex-fill">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <a id="sidebar-toggle" onClick={(e) => { e.preventDefault(); window.__toggleSidebar && window.__toggleSidebar(e); }}>
              <i className="fas fa-bars"></i>
            </a>
            <h5><i className="fas fa-plus-circle" style={{ marginRight: 8 }}></i>{t("createSharedEvent.title")}</h5>
          </div>
        </nav>
      </div>

      <div className="main-wrapper">
        <div className="create-event-container">
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 24 }}>
            {t("createSharedEvent.subtitle")}
          </p>

          <div className="create-event-name-section">
            <label className="create-event-label">{t("createSharedEvent.eventName")}</label>
            <input
              type="text"
              className="form-control create-event-name-input"
              placeholder={t("createSharedEvent.eventNamePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label className="create-event-label">{t("createSharedEvent.descriptionOptional")}</label>
            <textarea
              className="form-control create-event-name-input"
              placeholder={t("createSharedEvent.descriptionPlaceholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{ resize: "vertical", fontSize: 14 }}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label className="create-event-label">{t("createSharedEvent.actionsLabel")}</label>
            <p className="create-event-subtitle" style={{ marginBottom: 12 }}>
              {t("createSharedEvent.actionsDesc")}
            </p>

            {actions.map((action, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: `hsl(${(i * 60) % 360}, 65%, 50%)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, color: "#fff", fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</div>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("createSharedEvent.actionPlaceholder", { index: i + 1 })}
                  value={action.name}
                  onChange={(e) => updateAction(i, "name", e.target.value)}
                  style={{ flex: 1 }}
                />
                {actions.length > 1 && (
                  <button
                    onClick={() => removeAction(i)}
                    style={{
                      background: "rgba(255,69,58,0.15)", border: "none", borderRadius: 8,
                      width: 36, height: 36, color: "#FF453A", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={addAction}
              style={{
                background: "rgba(255,255,255,0.06)", border: "1px dashed rgba(255,255,255,0.15)",
                borderRadius: 10, padding: "10px 0", width: "100%", color: "rgba(255,255,255,0.4)",
                cursor: "pointer", fontSize: 13, fontWeight: 600, marginTop: 4,
              }}
            >
              <i className="fas fa-plus" style={{ marginRight: 6 }}></i>
              {t("createSharedEvent.addAction")}
            </button>
          </div>

          {error && <p style={{ color: "#FF453A", fontSize: 13, marginBottom: 12 }}>{error}</p>}

          <div className="create-event-actions">
            <button className="btn create-event-start-btn" onClick={handleCreate} disabled={saving}>
              {saving ? (
                <span className="spinner-border spinner-border-sm" role="status"></span>
              ) : (
                <>
                  <i className="fas fa-rocket" style={{ marginRight: 8 }}></i>
                  {t("createSharedEvent.createBtn")}
                </>
              )}
            </button>
            <button className="btn create-event-cancel-btn" onClick={() => navigate("/events")} disabled={saving}>
              {t("common.cancel")}
            </button>
          </div>

          <div className="create-event-warning">
            {t("createSharedEvent.afterCreating")}
          </div>
        </div>
      </div>
    </>
  );
}

export default CreateSharedEventPage;
